import {
  FpTabs as Tabs,
  FpTabsContent as TabsContent,
  FpTabsList as TabsList,
  FpTabsTrigger as TabsTrigger,
} from "@/components/ui/tabs";
import { useMcpServers } from "@/lib/hooks/useMcpServers";
import { useWebSocketMessaging } from "@/lib/hooks/useWebSocketMessaging";
import { useMessageStore } from "@/state/stores";
import { MessageSquare, Monitor, Settings } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { ChatInterface } from "./chat/ChatInterface";
import { DebugMonitor } from "./monitor/DebugMonitor";
import { SystemManagement } from "./system/SystemManagement";

interface ProjectDebugChatTabProps {
  projectId: string;
}

interface McpServer {
  id: string;
  name: string;
  status: string;
  url: string;
}

export function ProjectDebugChatTab({ projectId }: ProjectDebugChatTabProps) {
  // Real hooks for messaging and MCP servers
  const { sendMessage, connectionStatus } = useWebSocketMessaging();
  const { data: mcpServersData, isLoading: mcpLoading } =
    useMcpServers(projectId);

  // Get messages from the message store
  const { messages } = useMessageStore(
    useShallow((state) => ({
      messages: state.messages,
    })),
  );

  // Convert MCP server data to UI format
  const mcpServers: McpServer[] = Object.values(mcpServersData || {}).map(
    (server) => ({
      id: server.id,
      name: server.name,
      status: server.connectionState,
      url: server.url,
    }),
  );

  // Mock system prompt
  const systemPrompt = `You are an AI assistant for MCP server development and testing.

You have access to the following tools:
- search_files: Search for files in the project
- read_file: Read the contents of a file
- write_file: Write or update a file
- run_command: Execute shell commands

Please be helpful and provide clear explanations of your actions.`;

  return (
    <div className="h-full flex flex-col">
      <Tabs defaultValue="chat" className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Debug Chat
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            System Management
          </TabsTrigger>
          <TabsTrigger value="monitor" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Debug Monitor
          </TabsTrigger>
        </TabsList>

        {/* Debug Chat Tab */}
        <TabsContent value="chat" className="flex-1 flex flex-col">
          <ChatInterface
            messages={messages}
            connectionStatus={connectionStatus}
            onSendMessage={sendMessage}
          />
        </TabsContent>

        {/* System Management Tab */}
        <TabsContent value="system" className="flex-1">
          <SystemManagement
            systemPrompt={systemPrompt}
            mcpServers={mcpServers}
            mcpLoading={mcpLoading}
          />
        </TabsContent>

        {/* Debug Monitor Tab */}
        <TabsContent value="monitor" className="flex-1">
          <DebugMonitor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
